import { DefaultValues } from 'react-hook-form'
import { boolean, InferType, mixed, object, string } from 'yup'

import { licenceAgeValidator } from '../LicenceAgeValidator'
import { showLicenceNumberQuestion } from 'directFlow/pages/Drivers/utils/utils'
import { AsyncValidators } from 'directFlow/state/config'
import { requiredMessage } from 'directFlow/state/config/content'
import { licenceValidationErrorMessages } from 'directFlow/state/formManager/drivingLicenceValidator/drivingLicenceValidator.helpers'
import { isUKLicenceType } from 'directFlow/utils/general'

import { LicenceType } from 'types'

export const licencePageDefaultValues = {
  licenceType: undefined,
  countryOfFirstTest: undefined,
  ukDurationYear: undefined,
  ukDurationMonth: undefined,
  hasAnotherLicence: undefined,
  durationYear: undefined,
  durationMonth: undefined,
  drivingLicenceNumber: undefined,
  skipDrivingLicenceNumberQuestion: undefined,
} satisfies DefaultValues<LicenceSchemaType>

type Args = {
  asyncValidators: AsyncValidators
}

export const getLicenceSchema = ({ asyncValidators }: Args) =>
  object({
    licenceType: mixed<LicenceType>().oneOf(Object.values(LicenceType)).required(requiredMessage),
    ukDurationYear: string().when('licenceType', {
      is: (licenceType: LicenceType) => isUKLicenceType(licenceType),
      then: () =>
        string()
          .required(requiredMessage)
          .test({
            name: 'licence-age-validator',
            test: async (value, context) => {
              const res = licenceAgeValidator({
                dateOfBirth: context.options.context?.drivers.dateOfBirth,
                licenceType: context.parent.licenceType,
                dateLicenceReceived: {
                  year: value,
                  month: context.parent.ukDurationMonth,
                },
              })

              if (typeof res === 'string') {
                return context.createError({
                  type: 'licence-age-validator',
                  message: res,
                })
              }

              return true
            },
          }),

      otherwise: () => string().nullable().optional(),
    }),
    ukDurationMonth: string().when('licenceType', {
      is: (licenceType: LicenceType) => isUKLicenceType(licenceType),
      then: () =>
        string()
          .required(requiredMessage)
          .test({
            name: 'licence-age-validator',
            test: async (value, context) => {
              const res = licenceAgeValidator({
                dateOfBirth: context.options.context?.drivers.dateOfBirth,
                licenceType: context.parent.licenceType,
                dateLicenceReceived: {
                  year: context.parent.ukDurationYear,
                  month: value,
                },
              })
              if (typeof res === 'string') {
                return context.createError({
                  type: 'licence-age-validator',
                  message: res,
                })
              }

              return true
            },
          }),

      otherwise: () => string().nullable().optional(),
    }),
    hasAnotherLicence: boolean().when('licenceType', {
      is: (licenceType: LicenceType) => isUKLicenceType(licenceType),
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable().optional(),
    }),
    countryOfFirstTest: string().when(['hasAnotherLicence', 'licenceType'], {
      is: (hasAnotherLicence: boolean, licenceType: LicenceType) =>
        !hasAnotherLicence && isUKLicenceType(licenceType),
      then: () => string().nullable().optional(),
      otherwise: () => string().nullable().required(requiredMessage),
    }),
    durationYear: string().when(['hasAnotherLicence', 'licenceType'], {
      is: (hasAnotherLicence: boolean, licenceType: LicenceType) =>
        !hasAnotherLicence && isUKLicenceType(licenceType),
      then: () => string().nullable().optional(),
      otherwise: () =>
        string()
          .nullable()
          .required(requiredMessage)
          .test({
            name: 'licence-age-validator',
            test: async (value, context) => {
              const isFirstInternationalLicence =
                context.parent.countryOfFirstTest !== 'GB' && context.parent.hasAnotherLicence

              const res = licenceAgeValidator({
                dateOfBirth: context.options.context?.drivers.dateOfBirth,
                licenceType: context.parent.licenceType,
                dateLicenceReceived: {
                  year: value,
                  month: context.parent.durationMonth,
                },
                isFirstInternationalLicence,
              })

              if (typeof res === 'string') {
                return context.createError({
                  type: 'licence-age-validator',
                  message: res,
                })
              }

              return true
            },
          }),
    }),
    durationMonth: string().when(['hasAnotherLicence', 'licenceType'], {
      is: (hasAnotherLicence: boolean, licenceType: LicenceType) =>
        !hasAnotherLicence && isUKLicenceType(licenceType),
      then: () => string().nullable().optional(),
      otherwise: () =>
        string()
          .nullable()
          .required(requiredMessage)
          .test({
            name: 'licence-age-validator',
            test: async (value, context) => {
              const isFirstInternationalLicence =
                context.parent.countryOfFirstTest !== 'GB' && context.parent.hasAnotherLicence

              const res = licenceAgeValidator({
                dateOfBirth: context.options.context?.drivers.dateOfBirth,
                licenceType: context.parent.licenceType,
                dateLicenceReceived: {
                  year: context.parent.durationYear,
                  month: value,
                },
                isFirstInternationalLicence,
              })

              if (typeof res === 'string') {
                return context.createError({
                  type: 'licence-age-validator',
                  message: res,
                })
              }

              return true
            },
          }),
    }),
    skipDrivingLicenceNumberQuestion: boolean().optional(),
    drivingLicenceNumber: string()
      .nullable()
      .when(['skipDrivingLicenceNumberQuestion', 'licenceType'], {
        is: (
          skipDrivingLicenceNumberQuestion: boolean | undefined,
          licenceType: LicenceType | undefined,
        ) =>
          !skipDrivingLicenceNumberQuestion &&
          showLicenceNumberQuestion({
            licenceType,
          }),
        then: () =>
          string()
            .required(requiredMessage)
            .test({
              name: 'dln-validation',
              test: async (value, context) => {
                const res = await asyncValidators.drivingLicenceValidator({
                  licenceNumber: value ?? '',
                  dateOfBirth: context.options.context?.drivers.dateOfBirth,
                  lastName: context.options.context?.drivers.lastName,
                  title: context.options.context?.drivers.title,
                  firstName: context.options.context?.drivers.firstName,
                })

                if ('error' in res) {
                  return context.createError({
                    type: res.error,
                    message: licenceValidationErrorMessages()[res.error],
                  })
                }

                return true
              },
            }),
        otherwise: () => string().optional().nullable(),
      }),
  })

export type LicenceSchemaType = InferType<ReturnType<typeof getLicenceSchema>>
